import React, { useState } from 'react';
import { handleGoogleLogin, handleMicrosoftLogin, MICROSOFT_CLIENT_ID, MICROSOFT_OPEN_IN_TAB } from '../services/oauth';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const OAuthButtons = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState({ google: false, microsoft: false });
  const navigate = useNavigate();
  const { oauthLogin } = useAuth();

  const handleGoogleClick = async () => {
    setLoading(prev => ({ ...prev, google: true }));
    try {
      const result = await handleGoogleLogin();
      if (result) {
        const loginResult = await oauthLogin(result);
        if (loginResult === 'pending_approval') {
          navigate('/pending-approval');
        } else if (loginResult === 'requires_2fa') {
          navigate('/two-factor-verification');
        } else if (loginResult === 'suggest_2fa') {
          // Redirect to onboarding flow
          navigate('/onboarding');
        } else if (loginResult === true) {
          // Successful login - navigate to dashboard
          navigate('/dashboard');
        } else if (onLoginSuccess) {
          onLoginSuccess(result);
        }
      }
    } catch (error) {
      console.error('Google login error:', error);
      
      // Handle specific error cases with user-friendly messages
      let errorMessage = 'Google login failed';
      
      if (error.message) {
        if (error.message.includes('cancelled by user')) {
          errorMessage = 'Login cancelled. Please try again if you want to continue.';
        } else if (error.message.includes('timed out')) {
          errorMessage = 'Login timed out. Please try again.';
        } else if (error.message.includes('Access denied')) {
          errorMessage = 'Access denied. Please grant the required permissions.';
        } else if (error.message.includes('Popup blocked')) {
          errorMessage = 'Popup blocked by browser. Please allow popups for this site and try again.';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = error.message;
        }
      }
      
      // Only show toast for non-cancellation errors
      if (!error.message?.includes('cancelled by user')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(prev => ({ ...prev, google: false }));
    }
  };

  const handleMicrosoftClick = async () => {
    setLoading(prev => ({ ...prev, microsoft: true }));
    try {
      const result = await handleMicrosoftLogin();
      
      if (result === null) {
        // Popup was blocked; redirect flow started – user will land on Microsoft callback
        toast.loading('Redirecting to Microsoft…', { id: 'microsoft-redirect' });
        return;
      }
      
      if (result) {
        toast.dismiss('microsoft-redirect');
        const loginResult = await oauthLogin(result);
        
        if (loginResult === 'pending_approval') {
          navigate('/pending-approval');
        } else if (loginResult === 'requires_2fa') {
          navigate('/two-factor-verification');
        } else if (loginResult === 'suggest_2fa') {
          // Redirect to onboarding flow
          navigate('/onboarding');
        } else if (loginResult === true) {
          // Successful login - navigate to dashboard
          navigate('/dashboard');
        } else if (onLoginSuccess) {
          onLoginSuccess(result);
        }
      }
    } catch (error) {
      toast.dismiss('microsoft-redirect');
      console.error('Microsoft login error:', error);
      
      // Handle specific error cases with user-friendly messages
      let errorMessage = 'Microsoft login failed';
      
      if (error.message) {
        if (error.message.includes('cancelled by user')) {
          errorMessage = 'Login cancelled. Please try again if you want to continue.';
        } else if (error.message.includes('timed out')) {
          errorMessage = 'Login timed out. Please try again.';
        } else if (error.message.includes('already in progress')) {
          errorMessage = 'A Microsoft login is already in progress. Please complete it first.';
        } else if (error.message === MICROSOFT_OPEN_IN_TAB || (error.message && /block_nested_popups|running in a popup/i.test(error.message))) {
          // MSAL blocks when this tab was opened as a "popup" (e.g. target="_blank"). Show option to open login in a new tab with noopener so Microsoft sign-in works.
          const loginUrl = `${window.location.origin}/login`;
          toast.custom(
            (t) => (
              <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 shadow-lg dark:border-amber-700 dark:bg-amber-950/80">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Microsoft sign-in doesn&apos;t work in this window. Open the login page in a new tab and try again.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    window.open(loginUrl, '_blank', 'noopener,noreferrer');
                    toast.dismiss(t.id);
                  }}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Open login in new tab
                </button>
              </div>
            ),
            { duration: 10000 }
          );
          return;
        } else if (error.message.includes('Popup blocked') || error.message.includes('popup blocked')) {
          errorMessage = 'Popup blocked. Please allow popups for this site and try again.';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = error.message;
        }
      }
      
      // Only show toast for non-cancellation errors
      if (!error.message?.includes('cancelled by user')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(prev => ({ ...prev, microsoft: false }));
    }
  };

  return (
    <div className="space-y-3">
      {!MICROSOFT_CLIENT_ID && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                Microsoft OAuth is not configured. Please check your environment variables.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 !bg-gray-100 !text-gray-600">Or continue with</span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleGoogleClick}
          disabled={loading.google}
          className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm !bg-white !text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading.google ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-secondary-500"></div>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="ml-2">Google</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleMicrosoftClick}
          disabled={loading.microsoft || !MICROSOFT_CLIENT_ID}
          className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm !bg-white !text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading.microsoft ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-secondary-500"></div>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#f25022"
                  d="M1 1h10v10H1z"
                />
                <path
                  fill="#7fba00"
                  d="M13 1h10v10H13z"
                />
                <path
                  fill="#00a4ef"
                  d="M1 13h10v10H1z"
                />
                <path
                  fill="#ffb900"
                  d="M13 13h10v10H13z"
                />
              </svg>
              <span className="ml-2">
                {MICROSOFT_CLIENT_ID ? 'Microsoft' : 'Microsoft (Not Configured)'}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OAuthButtons; 